import Step from "./Step"
import Button from "../../Components/Button"
import * as Yup from "yup"
import FormikField from "../../Components/Forms/FormikField"
import MentorPreview from "../../Components/MentorPreview"
import { getStepMetadata } from "../../constants/stepRegistry"
import MentorFields from "../../Components/MentorFields"
import api from "../../services/api"
import { useAtom, useSetAtom } from "jotai"
import { fetchPlanAtom, planAtom, planTokenAtom, isGuideAtom, isGuideAdminAtom } from "../../state"
import { useIsAdmin } from "../../hooks/useIsAdmin"
import Spinner from "../../Components/Spinner"
import React, { useState, useEffect, useRef } from "react"
import {
  MAXIMUM_MENTOR_COUNT,
  MAXIMUM_SUGGESTED_MENTOR_COUNT,
  MINIMUM_MENTOR_COUNT,
} from "../../constants/mentorCount"
import {
  MENTOR_CIRCLE_ACTIVITY_URL,
  MENTORS_RESOURCE_URL,
  MENTORS_WORKSHEET_URL,
} from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"

const stepMetadata = getStepMetadata("mentorCircle")

const maxMembers = MAXIMUM_MENTOR_COUNT

const shape = {
  first_name: Yup.string().required("Required"),
  last_name: Yup.string().required("Required"),
  email: Yup.string().transform(v => (v === "" ? undefined : v)).email("Invalid email").optional(),
}
const schema = Yup.object().shape(shape)

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
}

const step = {
  name: stepMetadata.name,
  title: "Choose the people you trust.",
  type: stepMetadata.type,
  instructions: [
    `Choose the adults you trust to walk with you over the next year. These mentors will encourage you, pray for you and help you reach your goals.`,
    'Don\'t worry about picking "perfect" people. Just look for adults who you know will show up, speak life into you, and make you feel seen.',
    <MoreInfoVideoModal
      src={MENTORS_RESOURCE_URL}
      postText={" How to choose great Mentors"}
      title={"Tips for Choosing Mentors"}
      text={""}
      textClassName={"text-circle no-underline"}
    />,
    <MoreInfoVideoModal
      src={MENTORS_WORKSHEET_URL}
      title={"Mentors Worksheet"}
      postText={"Printable Mentors Worksheet"}
      text={""}
      type={"pdf"}
      textClassName={"text-circle no-underline"}
      icon={"fa-regular fa-file-pen"}
    />,
    <MoreInfoVideoModal
      src={MENTOR_CIRCLE_ACTIVITY_URL}
      title={"Mentor Circle Worksheet"}
      postText={"Printable Mentor Circle Worksheet"}
      text={""}
      type={"pdf"}
      textClassName={"text-circle no-underline"}
      icon={"fa-regular fa-file-pen"}
    />,
  ],
  discussionQuestions: [
    "How have these adults encouraged or supported you in the past?",
    "In what ways do you hope each person might walk with you this year?",
    "Was there anyone you almost included, but didn't? Why?",
  ],
}

function MentorCircle() {
  const [planToken] = useAtom(planTokenAtom)
  const fetchPlan = useSetAtom(fetchPlanAtom)
  const setPlan = useSetAtom(planAtom)
  const [isGuide] = useAtom(isGuideAtom)
  const [isGuideAdmin] = useAtom(isGuideAdminAtom)
  const isAdmin = useIsAdmin()
  const canInlineEdit = isGuide || isGuideAdmin || isAdmin
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [legacyMembers, setLegacyMembers] = useState([])
  const [refreshCounter, setRefreshCounter] = useState(0)
  const firstNameRef = useRef(null)

  // Parse legacy name string into first_name/last_name
  const parseLegacyMember = planContentItem => {
    const nameParts = (planContentItem.value || "").trim().split(/\s+/)
    return {
      id: planContentItem.id, // PlanContent ID for reference
      legacy_name: planContentItem.value,
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
      email: "",
      is_legacy: true,
    }
  }

  // Check if legacy member has been migrated by matching names
  const isAlreadyMigrated = (legacy, newMentors) => {
    return newMentors.some(mentor => {
      const legacyFullName = `${legacy.first_name} ${legacy.last_name}`.trim()
      const mentorFullName = `${mentor.first_name} ${mentor.last_name}`.trim()
      return legacyFullName.toLowerCase() === mentorFullName.toLowerCase()
    })
  }

  // Fetch legacy members at the top level
  useEffect(() => {
    if (!planToken) return

    const fetchLegacyMembers = async () => {
      try {
        const [legacyResponse, mentorsResponse] = await Promise.all([
          api.getPlanContent(planToken, ["members"]),
          api.getMentors(planToken),
        ])

        const newMentors = mentorsResponse.data.mentors || []

        // Parse legacy members and filter out already migrated ones
        const parsedLegacy = (legacyResponse.data || [])
          .filter(item => item.key === "members")
          .map(item => parseLegacyMember(item))
          .filter(legacy => !isAlreadyMigrated(legacy, newMentors))

        setLegacyMembers(parsedLegacy)
      } catch (error) {
        console.error("Error fetching legacy members:", error)
      }
    }

    fetchLegacyMembers()
  }, [planToken, refreshCounter])

  const getPreviewData = data => {
    return {
      mentors: data.mentors || [],
      legacyMembers,
      totalCount: (data.mentors || []).length + legacyMembers.length,
    }
  }

  const getIsComplete = data => {
    const mentors = data.mentors || []
    const totalMentors = mentors.length + legacyMembers.length
    return totalMentors >= MINIMUM_MENTOR_COUNT && totalMentors <= MAXIMUM_MENTOR_COUNT
  }

  const onSubmit = async (values, actions, onSubmitCallback) => {
    setIsSubmitting(true)
    try {
      if (values.email) {
        const response = await api.addMentors(planToken, [
          {
            email: values.email,
            first_name: values.first_name,
            last_name: values.last_name,
          },
        ])
        // If the backend auto-progressed the plan status (e.g. SitWithStudent →
        // InviteMentors), patch the atom so the invite UI appears without a full
        // re-fetch that would close the form.
        const newStatus = response.data?.plan_status
        if (newStatus) {
          setPlan(prev => ({ ...prev, status: newStatus }))
        }
      } else {
        await api.createPlanContent({
          reset: false,
          content: [
            {
              plan_token: planToken,
              key: "members",
              value: `${values.first_name} ${values.last_name}`.trim(),
            },
          ],
        })
      }
      // Keep modal open in edit mode: toggleEditing=false prevents switching to preview
      await onSubmitCallback(undefined, false, false)
      setRefreshCounter(prev => prev + 1)
      actions.resetForm()
      // Focus the first name field after reset
      setTimeout(() => {
        firstNameRef.current?.focus()
      }, 0)
    } catch (error) {
      console.error("Error adding mentor:", error)
      actions.setSubmitting(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Step
      id="mentors"
      step={step}
      Preview={MentorPreview}
      getIsComplete={getIsComplete}
      getPreviewData={getPreviewData}
      keys={["mentors"]}
      schema={schema}
      onSubmit={onSubmit}
      image={stepMetadata.image}
      theme={stepMetadata.theme}
      description={stepMetadata.description}
      customFetch={true}
      initialValues={initialValues}
      onMigrate={() => setRefreshCounter(prev => prev + 1)}
    >
      {(values, data, refreshData) => (
        <>
          <FormikField
            primary={true}
            name="mentors"
            instructions={step.instructions}
            title={step.title}
          >
            <h3 className="mb-2 text-lg font-bold">Discussion Questions</h3>
            <p className="mb-2">
              Use these prompts to reflect on who you chose and why they matter to you.
            </p>
            <ul className="mb-4 list-inside list-disc">
              {step.discussionQuestions.map((question, index) => (
                <li className={"font-bold"} key={index}>
                  {question}
                </li>
              ))}
            </ul>
            <div className="mb-4 text-sm">
              List your Mentor Circle Members below (up to {MAXIMUM_SUGGESTED_MENTOR_COUNT}):
              {legacyMembers.length > 0 && (
                <span className="ml-2 text-xs text-yellow-700">
                  ({(data.mentors || []).length} with emails, {legacyMembers.length} needing emails)
                </span>
              )}
            </div>
            <MentorPreview
              data={{
                mentors: data.mentors || [],
                legacyMembers: legacyMembers,
                totalCount: (data.mentors || []).length + legacyMembers.length,
              }}
              theme="circle"
              deletable={true}
              onDelete={refreshData}
              isEditing={true}
              canInvite={true}
              planToken={planToken}
              onMigrate={async () => {
                await refreshData()
                setRefreshCounter(prev => prev + 1)
              }}
              onInvite={refreshData}
              inlineEditEnabled={canInlineEdit}
              onUpdate={async () => {
                await refreshData()
                setRefreshCounter(prev => prev + 1)
              }}
            />
            {(data.mentors || []).length + legacyMembers.length < maxMembers && (
              <MentorFields firstNameRef={firstNameRef} />
            )}
          </FormikField>
          {(data.mentors || []).length + legacyMembers.length < maxMembers && (
            <Button bgColor="bg-circle hover:bg-circle/90" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <div className={"flex flex-row items-center justify-start gap-1"}>
                  <Spinner size={"sm"} />
                  <span>Saving...</span>
                </div>
              ) : (
                "Add Mentor Circle Member"
              )}
            </Button>
          )}
        </>
      )}
    </Step>
  )
}

export default MentorCircle
